import { useEffect } from 'react';
import { APP_STORAGE } from '../../_Config/AppConfig.Storage';
import { TABLE_TEMPLATE_IDS } from './_UI/Table/_TableTemplates';
import { ALERT_TEMPLATE_IDS } from './_UI/Alerts/_AlertTemplates';
import { HEADER_TEMPLATE_IDS } from './_UI/Header/_HeaderTemplates';
import { APP_ENVIRONMENT } from '../../_Config/_ENV/AppEnvironment';
import { CONFIG_PLUGIN } from "../../_Config/Plugins/_Config.Plugin";
import { LAYOUT_TEMPLATE_IDS } from './_UI/Layouts/_LayoutTemplates';
import { CONFIRM_TEMPLATE_IDS } from './_UI/Confirm/_ConfirmTemplates';
import { RICHTEXT_TEMPLATE_IDS } from './_UI/RichText/_RichTextTemplates';
import { TOAST_POSITION_IDS } from './_UI/Alerts/_Templates/_toastPosition';
import { STORAGE_SESSION_HELPER } from '../../HELPERS/storageSession.helper';
import { APP_SWITCH_APPEARANCE } from "../../_Config/Plugins/AppSwitchAppearance";
import { onSavePreference, onResolvePreference, onSyncPreferencesBase } from './_Hooks/useAppPreferences';
import { FONT_TEMPLATE_IDS, onResolveFontTemplate, onLoadFontTemplate } from './_UI/Fonts/_FontTemplates';

export const THEME_DARK = APP_ENVIRONMENT.APP__MODE_THEME_DARK;
export const THEME_LIGHT = APP_ENVIRONMENT.APP__MODE_THEME_LIGHT;
export const TEMPLATE_LEVELS_DEFINED = [0, 1, 2, 3, 4, 5];

// ---------------------------- 🔥 EVENTOS
export const TABLE_TEMPLATE_EVENT = 'ls:table_template';
export const ALERT_TEMPLATE_EVENT = 'ls:alert_template';
export const ALERT_POSITION_EVENT = 'ls:alert_position';
export const LAYOUT_TEMPLATE_EVENT = 'ls:layout_template';
export const HEADER_TEMPLATE_EVENT = 'ls:header_template';
export const CONFIRM_TEMPLATE_EVENT = 'ls:confirm_template';
export const RICHTEXT_TEMPLATE_EVENT = 'ls:richtext_template';

const IS_REMEMBER = () => CONFIG_PLUGIN.TEMPLATES.HAS__TEMPLATE_PREVIEW_REMEMBER === true;

export const onResolveLevel = (ids, level) => {
    if (typeof level === 'string' && ids.includes(level)) return level;
    const INDEX = Number(level) - 1;
    if (!Number.isInteger(INDEX) || INDEX < 0 || INDEX >= ids.length) return ids[0];
    return ids[INDEX];
};

const onRemember = (key, value) => {
    if (IS_REMEMBER()) onSavePreference(key, value);
};

export const onApplyTemplate = (level) => {
    const SAFE = TEMPLATE_LEVELS_DEFINED.includes(Number(level)) ? Number(level) : 0;
    document.documentElement.setAttribute('data-ls-template', SAFE);
    onRemember('template', SAFE);
    return SAFE;
};

export const onReadTemplate = () => {
    const ATTR = document.documentElement.getAttribute('data-ls-template');
    if (ATTR !== null) return Number(ATTR);
    return Number(onResolvePreference('template', APP_SWITCH_APPEARANCE.THEME.AESTHETIC, IS_REMEMBER()));
};

export const onApplyLayoutTemplate = (id) => {
    const SAFE = LAYOUT_TEMPLATE_IDS.includes(Number(id)) ? Number(id) : LAYOUT_TEMPLATE_IDS[0];
    document.documentElement.setAttribute('data-ls-layout', SAFE);
    window.dispatchEvent(new CustomEvent(LAYOUT_TEMPLATE_EVENT, { detail: { id: SAFE } }));
    onRemember('layout', SAFE);
    return SAFE;
};

export const onReadLayoutTemplate = () => {
    const ATTR = document.documentElement.getAttribute('data-ls-layout');
    if (ATTR !== null) return Number(ATTR);
    return Number(onResolvePreference('layout', APP_SWITCH_APPEARANCE.LAYOUT.STRUCTURE, IS_REMEMBER()));
};

export const onApplyTableTemplate = (id) => {
    const SAFE = TABLE_TEMPLATE_IDS.includes(id) ? id : TABLE_TEMPLATE_IDS[0];
    document.documentElement.setAttribute('data-ls-table', SAFE);
    window.dispatchEvent(new CustomEvent(TABLE_TEMPLATE_EVENT, { detail: { id: SAFE } }));
    onRemember('table', SAFE);
    return SAFE;
};

export const onReadTableTemplate = () => (
    document.documentElement.getAttribute('data-ls-table') ?? onResolvePreference('table', onResolveLevel(TABLE_TEMPLATE_IDS, APP_SWITCH_APPEARANCE.STYLES.DASHBOARD_TABLES), IS_REMEMBER())
    ?? TABLE_TEMPLATE_IDS[0]
);

export const onApplyFontTemplate = (id) => {
    const SAFE = FONT_TEMPLATE_IDS.includes(id) ? id : FONT_TEMPLATE_IDS[0];
    const TEMPLATE = onResolveFontTemplate(SAFE);
    onLoadFontTemplate(TEMPLATE);
    const ROOT = document.documentElement;
    ROOT.setAttribute('data-ls-font', SAFE);
    ROOT.style.setProperty('--LS__app_font_base', TEMPLATE.base);
    ROOT.style.setProperty('--LS__app_font_title', TEMPLATE.title);
    onRemember('font', SAFE);
    return SAFE;
};

export const onReadFontTemplate = () => (
    document.documentElement.getAttribute('data-ls-font') ?? onResolvePreference('font', onResolveLevel(FONT_TEMPLATE_IDS, APP_SWITCH_APPEARANCE.LAYOUT.FONT_APP), IS_REMEMBER())
    ?? FONT_TEMPLATE_IDS[0]
);

export const onApplyHeaderTemplate = (scope, id) => {
    const SAFE = HEADER_TEMPLATE_IDS.includes(Number(id)) ? Number(id) : HEADER_TEMPLATE_IDS[0];
    document.documentElement.setAttribute(`data-ls-header-${scope}`, SAFE);
    window.dispatchEvent(new CustomEvent(HEADER_TEMPLATE_EVENT, { detail: { scope, id: SAFE } }));
    onRemember(`header_${scope}`, SAFE);
    return SAFE;
};

export const onReadHeaderTemplate = (scope, fallback) => {
    const ATTR = document.documentElement.getAttribute(`data-ls-header-${scope}`);
    if (ATTR !== null) return Number(ATTR);
    return Number(onResolvePreference(`header_${scope}`, fallback, IS_REMEMBER()));
};

export const onApplyAlertTemplate = (id) => {
    const SAFE = ALERT_TEMPLATE_IDS.includes(id) ? id : ALERT_TEMPLATE_IDS[0];
    document.documentElement.setAttribute('data-ls-alert', SAFE);
    window.dispatchEvent(new CustomEvent(ALERT_TEMPLATE_EVENT, { detail: { id: SAFE } }));
    onRemember('alert', SAFE);
    return SAFE;
};

export const onReadAlertTemplate = () => (
    document.documentElement.getAttribute('data-ls-alert') ?? onResolvePreference('alert', onResolveLevel(ALERT_TEMPLATE_IDS, APP_SWITCH_APPEARANCE.STYLES.ALERT), IS_REMEMBER())
    ?? ALERT_TEMPLATE_IDS[0]
);

export const onApplyAlertPosition = (id) => {
    const SAFE = TOAST_POSITION_IDS.includes(id) ? id : 'top-right';
    document.documentElement.setAttribute('data-ls-alert-position', SAFE);
    window.dispatchEvent(new CustomEvent(ALERT_POSITION_EVENT, { detail: { id: SAFE } }));
    onRemember('alert_position', SAFE);
    return SAFE;
};

export const onReadAlertPosition = () => (
    document.documentElement.getAttribute('data-ls-alert-position') ?? onResolvePreference('alert_position', onResolveLevel(TOAST_POSITION_IDS, APP_SWITCH_APPEARANCE.STYLES.ALERT_POSITION), IS_REMEMBER())
    ?? 'top-right'
);

export const onApplyConfirmTemplate = (id) => {
    const SAFE = CONFIRM_TEMPLATE_IDS.includes(id) ? id : CONFIRM_TEMPLATE_IDS[0];
    document.documentElement.setAttribute('data-ls-confirm', SAFE);
    window.dispatchEvent(new CustomEvent(CONFIRM_TEMPLATE_EVENT, { detail: { id: SAFE } }));
    onRemember('confirm', SAFE);
    return SAFE;
};

export const onReadConfirmTemplate = () => (
    document.documentElement.getAttribute('data-ls-confirm') ?? onResolvePreference('confirm', onResolveLevel(CONFIRM_TEMPLATE_IDS, APP_SWITCH_APPEARANCE.STYLES.CONFIRM_ALERT), IS_REMEMBER())
    ?? CONFIRM_TEMPLATE_IDS[0]
);

export const onApplyRichTextTemplate = (id) => {
    const SAFE = RICHTEXT_TEMPLATE_IDS.includes(id) ? id : RICHTEXT_TEMPLATE_IDS[0];
    document.documentElement.setAttribute('data-ls-richtext', SAFE);
    window.dispatchEvent(new CustomEvent(RICHTEXT_TEMPLATE_EVENT, { detail: { id: SAFE } }));
    onRemember('richtext', SAFE);
    return SAFE;
};

export const onReadRichTextTemplate = () => (
    document.documentElement.getAttribute('data-ls-richtext') ?? onResolvePreference('richtext', onResolveLevel(RICHTEXT_TEMPLATE_IDS, APP_SWITCH_APPEARANCE.STYLES.RICH_EDITOR), IS_REMEMBER())
    ?? RICHTEXT_TEMPLATE_IDS[0]
);

// Portfolio Bailador: tema oscuro fijo. Se mantiene la API publica para no
// romper el Core, pero ninguna llamada puede cambiar el documento a light.
export const onApplyTheme = () => {
    document.documentElement.setAttribute('data-theme', THEME_DARK);
    document.documentElement.setAttribute('data-bs-theme', THEME_DARK);
    document.documentElement.style.colorScheme = THEME_DARK;
    try { STORAGE_SESSION_HELPER.SET_STORAGE(APP_STORAGE.APP_THEME, THEME_DARK); } catch { /* nada */ }
    return THEME_DARK;
};

export const onReadTheme = () => THEME_DARK;

// ---------------------------- 🔥 APARIENCIA DECLARADA
export const APPEARANCE_ATTRIBUTES = {
    'color-variant': (A) => A.THEME.PRIMARY_COLOR_VARIANT,

    'layout-auth': (A) => A.LAYOUT.AUTH,
    'layout-public': (A) => A.LAYOUT.PUBLIC,
    'layout-private': (A) => A.LAYOUT.PRIVATE,
    'layout-dashboard': (A) => A.LAYOUT.DASHBOARD,

    'brand-header-public': (A) => A.BRAND.HEADER_PUBLIC.appearance,
    'brand-header-private': (A) => A.BRAND.HEADER_PRIVATE.appearance,
    'brand-footer-public': (A) => A.BRAND.FOOTER_PUBLIC.appearance,
    'brand-footer-private': (A) => A.BRAND.FOOTER_PRIVATE.appearance,

    'footer-public': (A) => A.STYLES.FOOTER_PUBLIC,
    'footer-private': (A) => A.STYLES.FOOTER_PRIVATE,
    'header-extra-ad-public': (A) => A.STYLES.HEADER_TOP_EXTRA_AD_PUBLIC,
    'header-extra-ad-private': (A) => A.STYLES.HEADER_TOP_EXTRA_AD_PRIVATE,
    'header-extra-offers-public': (A) => A.STYLES.HEADER_TOP_EXTRA_OFFERS_PUBLIC,
    'header-extra-offers-private': (A) => A.STYLES.HEADER_TOP_EXTRA_OFFERS_PRIVATE,
    'header-extra-celebrity-public': (A) => A.STYLES.HEADER_TOP_EXTRA_CELEBRITY_PUBLIC,
    'header-extra-celebrity-private': (A) => A.STYLES.HEADER_TOP_EXTRA_CELEBRITY_PRIVATE,

    'home-hero': (A) => A.STYLES.HOME_HERO,
    'home-contact': (A) => A.STYLES.HOME_CONTACT,
    'home-services': (A) => A.STYLES.HOME_SERVICES,
    'home-projects': (A) => A.STYLES.HOME_PROJECTS,
    'home-about-me': (A) => A.STYLES.HOME_ABOUT_ME,

    'blog-grid': (A) => A.STYLES.BLOG_GRID,
    'blog-card': (A) => A.STYLES.BLOG_CARD,
    'blog-single-post': (A) => A.STYLES.BLOG_SINGLE_POST,

    'work-with-us-form': (A) => A.STYLES.WORK_WITH_US_FORM,

    'auth-portal-client': (A) => A.STYLES.AUTH_PORTAL_CLIENT,
    'auth-staff-login': (A) => A.STYLES.AUTH_STAFF_LOGIN,

    'dashboard-cards': (A) => A.STYLES.DASHBOARD_CARDS,
    'dashboard-sidebar': (A) => A.STYLES.DASHBOARD_SIDEBAR,

    'align-footer-public': (A) => A.ALIGN.FOOTER_PUBLIC_ITEMS,
    'align-footer-private': (A) => A.ALIGN.FOOTER_PRIVATE_ITEMS,
    'align-nav-public': (A) => A.ALIGN.NAV_MENU_ITEMS_PUBLIC,
    'align-nav-private': (A) => A.ALIGN.NAV_MENU_ITEMS_PRIVATE,
    'align-header-top-public': (A) => A.ALIGN.HEADER_TOP_PUBLIC_ITEMS,
    'align-header-top-private': (A) => A.ALIGN.HEADER_TOP_PRIVATE_ITEMS,
};

const onSizeValue = (value) => (!value || value === 'auto' ? null : value);

export const BRAND_SIZE_VARIABLES = {
    '--LS__apr_brand_header_public_width': (A) => onSizeValue(A.BRAND.HEADER_PUBLIC.width),
    '--LS__apr_brand_header_public_height': (A) => onSizeValue(A.BRAND.HEADER_PUBLIC.height),
    '--LS__apr_brand_header_private_width': (A) => onSizeValue(A.BRAND.HEADER_PRIVATE.width),
    '--LS__apr_brand_header_private_height': (A) => onSizeValue(A.BRAND.HEADER_PRIVATE.height),
    '--LS__apr_brand_footer_public_width': (A) => onSizeValue(A.BRAND.FOOTER_PUBLIC.width),
    '--LS__apr_brand_footer_public_height': (A) => onSizeValue(A.BRAND.FOOTER_PUBLIC.height),
    '--LS__apr_brand_footer_private_width': (A) => onSizeValue(A.BRAND.FOOTER_PRIVATE.width),
    '--LS__apr_brand_footer_private_height': (A) => onSizeValue(A.BRAND.FOOTER_PRIVATE.height),
};

export const onApplyAppearance = (appearance = APP_SWITCH_APPEARANCE) => {

    if (typeof document === 'undefined') return {};

    const ROOT = document.documentElement;
    const APPLIED = {};

    Object.entries(APPEARANCE_ATTRIBUTES).forEach(([attr, onPick]) => {
        const VALUE = onPick(appearance);
        if (VALUE === undefined || VALUE === null) return;
        ROOT.setAttribute(`data-ls-${attr}`, VALUE);
        APPLIED[attr] = VALUE;
    });

    Object.entries(BRAND_SIZE_VARIABLES).forEach(([variable, onPick]) => {
        const VALUE = onPick(appearance);
        if (!VALUE) return;
        ROOT.style.setProperty(variable, VALUE);
        APPLIED[variable] = VALUE;
    });

    return APPLIED;
};

// ---------------------------- 🔥 RESOLVER
export const AppTemplateResolved = ({ children }) => {

    useEffect(() => {
        onSyncPreferencesBase(APP_SWITCH_APPEARANCE);
        onApplyAppearance();
        onApplyTemplate(onReadTemplate());
        onApplyLayoutTemplate(onReadLayoutTemplate());
        onApplyTableTemplate(onReadTableTemplate());
        onApplyFontTemplate(onReadFontTemplate());
        onApplyHeaderTemplate('public', onReadHeaderTemplate('public', APP_SWITCH_APPEARANCE.STYLES.HEADER_TOP_PUBLIC));
        onApplyHeaderTemplate('private', onReadHeaderTemplate('private', APP_SWITCH_APPEARANCE.STYLES.HEADER_TOP_PRIVATE));
        onApplyAlertTemplate(onReadAlertTemplate());
        onApplyAlertPosition(onReadAlertPosition());
        onApplyConfirmTemplate(onReadConfirmTemplate());
        onApplyRichTextTemplate(onReadRichTextTemplate());
        onApplyTheme(onReadTheme());
    }, []);

    return <>{children}</>;
};
